import React, {useState, useEffect, useCallback} from 'react';
import {Container, Row, Col, Button, Spinner, Alert, Nav, Tab, Badge, Tooltip, OverlayTrigger} from 'react-bootstrap';
import {toast} from 'react-toastify';
import {
  KPICard,
  AnalyticsFilters,
  LineChart,
  BarChart,
  PieChart,
  HeatmapChart,
  DrillDownModal
} from 'components/analytics';
import {api} from 'api';
import './_analytics.scss';
import {CURRENCY} from 'common/constants';
import {
  FaSync,
  FaDownload,
  FaChartLine,
  FaUsers,
  FaMapMarkerAlt,
  FaCalendarCheck,
  FaExclamationTriangle,
  FaCheckCircle,
  FaInfoCircle,
  FaDollarSign,
  FaBuilding,
  FaUserFriends,
  FaClipboardList,
  FaClock
} from 'react-icons/fa';

const Analytics = () => {
  // Helper function to format relative time
  const getRelativeTime = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    }
  };

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    ground_id: '',
    sport_id: '',
    city: '',
    period: 'daily',
    breakdown_by: 'sport',
    limit: 10
  });

  const [data, setData] = useState({
    kpis: null,
    bookingTrends: [],
    revenueBreakdown: [],
    groundPerformance: [],
    userActivity: [],
    bookingHeatmap: []
  });

  const [error, setError] = useState(null);
  const [drillDownModal, setDrillDownModal] = useState({
    show: false,
    type: null,
    title: ''
  });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [dataStatus, setDataStatus] = useState('loading'); // loading, loaded, error, empty

  const loadAnalyticsData = useCallback(async (isRefresh = false, customFilters = null) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    setDataStatus('loading');

    try {
      // Use custom filters if provided, otherwise use current state
      const currentFilters = customFilters || filters;
      const params = {
        ...currentFilters,
        start_date: currentFilters.start_date || undefined,
        end_date: currentFilters.end_date || undefined,
        ground_id: currentFilters.ground_id || undefined,
        sport_id: currentFilters.sport_id || undefined,
        city: currentFilters.city || undefined
      };

      // Remove empty values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === undefined) {
          delete params[key];
        }
      });

      const [
        kpisRes,
        trendsRes,
        revenueRes,
        groundRes,
        activityRes,
        heatmapRes
      ] = await Promise.all([
        api.getAnalyticsKPIs(params),
        api.getBookingTrends(params),
        api.getRevenueBreakdown(params),
        api.getGroundPerformance(params),
        api.getUserActivity(params),
        api.getBookingHeatmap(params)
      ]);

      // Convert string values to numbers for KPIs
      const processedKPIs = kpisRes ? {
        ...kpisRes,
        total_revenue: parseFloat(kpisRes.total_revenue) || 0,
        avg_booking_value: parseFloat(kpisRes.avg_booking_value) || 0,
        conversion_rate: parseFloat(kpisRes.conversion_rate) || 0
      } : null;

      // Process revenue breakdown data
      const processedRevenueBreakdown = (revenueRes || []).map(item => ({
        ...item,
        amount: parseFloat(item.amount) || 0,
        percentage: parseFloat(item.percentage) || 0
      }));

      // Process ground performance data
      const processedGroundPerformance = (groundRes || []).map(item => ({
        ...item,
        total_revenue: parseFloat(item.total_revenue) || 0,
        avg_rating: parseFloat(item.avg_rating) || 0,
        utilization_rate: parseFloat(item.utilization_rate) || 0
      }));

      // Process booking trends data
      const processedBookingTrends = (trendsRes || []).map(item => ({
        ...item,
        revenue: parseFloat(item.revenue) || 0
      }));

      const hasData = processedKPIs || processedBookingTrends.length > 0 ||
        processedRevenueBreakdown.length > 0 || processedGroundPerformance.length > 0;

      setData({
        kpis: processedKPIs,
        bookingTrends: processedBookingTrends,
        revenueBreakdown: processedRevenueBreakdown,
        groundPerformance: processedGroundPerformance,
        userActivity: activityRes || [],
        bookingHeatmap: heatmapRes || []
      });

      setDataStatus(hasData ? 'loaded' : 'empty');
      setLastUpdated(new Date());

      if (isRefresh) {
        toast.success('Data refreshed successfully');
      }
    } catch (error) {
      console.error('Error loading analytics data:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load analytics data. Please try again.';
      setError(errorMessage);
      setDataStatus('error');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setInitialLoading(false);
    }
  }, []); // Remove filters dependency to prevent automatic reloads

  useEffect(() => {
    loadAnalyticsData();
  }, []); // Only load on initial mount

  // Auto-refresh every 5 minutes for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      if (!loading && !refreshing) {
        loadAnalyticsData(true);
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [loading, refreshing, loadAnalyticsData]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    // Trigger data reload with new filters
    loadAnalyticsData(false, newFilters);
  };

  const handleExport = async (type, format = 'csv') => {
    try {
      setExportLoading(true);
      const params = {
        type,
        fmt: format,
        ...filters,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        ground_id: filters.ground_id || undefined,
        sport_id: filters.sport_id || undefined,
        city: filters.city || undefined
      };

      // Remove empty values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await api.exportAnalytics(params);

      // Create download link
      const blob = new Blob([response.data], {
        type: format === 'csv' ? 'text/csv' : 'application/json'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analytics_${type}_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} exported successfully`);
    } catch (error) {
      console.error('Error exporting data:', error);
      const errorMessage = error.response?.data?.message || 'Failed to export data';
      toast.error(errorMessage);
    } finally {
      setExportLoading(false);
    }
  };

  const handleRefresh = () => {
    loadAnalyticsData(true);
  };

  const handleKPIClick = (kpiType) => {
    setDrillDownModal({
      show: true,
      type: kpiType,
      title: `Detailed ${kpiType.charAt(0).toUpperCase() + kpiType.slice(1)} View`
    });
  };

  const handleCloseDrillDown = () => {
    setDrillDownModal({
      show: false,
      type: null,
      title: ''
    });
  };

  // System Overview Tab Component
  const SystemOverviewTab = () => (
    <>
      {/* KPIs Section */}
      <Row className="g-4 mb-4">
        <Col md={3}>
          <KPICard
            title="Total Users"
            value={data.kpis.total_users}
            subtitle={`${data.kpis.total_players} players, ${data.kpis.total_ground_owners} ground owners`}
            color="primary"
            icon={<FaUserFriends/>}
            onClick={() => handleKPIClick('users')}
          />
        </Col>
        <Col md={3}>
          <KPICard
            title="Total Grounds"
            value={data.kpis.total_grounds}
            subtitle={`${data.kpis.active_grounds} active, ${data.kpis.inactive_grounds} inactive`}
            color="success"
            icon={<FaBuilding/>}
            onClick={() => handleKPIClick('grounds')}
          />
        </Col>
        <Col md={3}>
          <KPICard
            title="Total Bookings"
            value={data.kpis.total_bookings}
            subtitle={`${data.kpis.confirmed_bookings} confirmed, ${data.kpis.completed_bookings} completed`}
            color="info"
            icon={<FaClipboardList/>}
            onClick={() => handleKPIClick('bookings')}
          />
        </Col>
        <Col md={3}>
          <KPICard
            title="Total Revenue"
            value={`${CURRENCY} ${data.kpis.total_revenue.toLocaleString()}`}
            subtitle={`Avg: ${CURRENCY} ${data.kpis.avg_booking_value.toFixed(2)} | Conversion: ${data.kpis.conversion_rate.toFixed(1)}%`}
            color="warning"
            icon={<FaDollarSign/>}
            onClick={() => handleKPIClick('revenue')}
          />
        </Col>
      </Row>

      {/* System Performance Overview */}
      <Row className="g-4">
        <Col lg={12}>
          <LineChart
            data={data.bookingTrends}
            title="System Performance Overview"
            dataKey="bookings_count"
            xAxisKey="date"
            color="#007bff"
            height={350}
          />
        </Col>
      </Row>
      <Row className="g-4">
        <Col lg={12}>
          <PieChart
            data={data.revenueBreakdown}
            title="Revenue Distribution"
            dataKey="amount"
            nameKey="category"
            height={350}
          />
        </Col>
      </Row>
    </>
  );

  // Bookings & Revenue Tab Component
  const BookingsRevenueTab = () => (
    <>
      <Row className="g-4 mb-4">
        <Col lg={6}>
          <LineChart
            data={data.bookingTrends}
            title="Booking Trends Over Time"
            dataKey="bookings_count"
            xAxisKey="date"
            color="#007bff"
            height={350}
          />
        </Col>
        <Col lg={6}>
          <PieChart
            data={data.revenueBreakdown}
            title="Revenue Breakdown by Sport"
            dataKey="amount"
            nameKey="category"
            height={350}
          />
        </Col>
      </Row>

      <Row className="g-4">
        <Col lg={12}>
          <LineChart
            data={data.bookingTrends}
            title="Revenue Trends Over Time"
            dataKey="revenue"
            xAxisKey="date"
            color="#28a745"
            height={300}
          />
        </Col>
      </Row>
    </>
  );

  // Ground Performance Tab Component
  const GroundPerformanceTab = () => (
    <>
      <Row className="g-4 mb-4">
        <Col lg={6}>
          <BarChart
            data={data.groundPerformance}
            title="Top Performing Grounds"
            dataKey="total_revenue"
            xAxisKey="ground_name"
            color="#28a745"
            height={350}
          />
        </Col>
        <Col lg={6}>
          <BarChart
            data={data.groundPerformance}
            title="Ground Utilization Rates"
            dataKey="utilization_rate"
            xAxisKey="ground_name"
            color="#17a2b8"
            height={350}
          />
        </Col>
      </Row>

      <Row className="g-4">
        <Col lg={12}>
          <HeatmapChart
            data={data.bookingHeatmap}
            title="Booking Activity Heatmap"
            height={350}
          />
        </Col>
      </Row>
    </>
  );

  // User Activity Tab Component
  const UserActivityTab = () => (
    <>
      <Row className="g-4 mb-4">
        <Col lg={12}>
          <LineChart
            data={data.userActivity}
            title="User Activity Over Time"
            dataKey="new_users"
            xAxisKey="date"
            color="#17a2b8"
            height={350}
          />
        </Col>
      </Row>
      <Row className="g-4 mb-4">
        <Col lg={12}>
          {(() => {
            const players = Number(data.kpis?.total_players || 0);
            const owners = Number(data.kpis?.total_ground_owners || 0);
            const total = players + owners;
            const playersPct = total > 0 ? Math.round((players / total) * 100) : 0;
            const ownersPct = total > 0 ? Math.round((owners / total) * 100) : 0;
            const pieData = [
              { category: 'Players', amount: players, percentage: playersPct },
              { category: 'Ground Owners', amount: owners, percentage: ownersPct },
            ];
            return (
              <PieChart
                data={pieData}
                title="User Distribution"
                dataKey="amount"
                nameKey="category"
                valuePrefix=""
                height={350}
              />
            );
          })()}
        </Col>
      </Row>

    </>
  );

  if (error) {
    return (
      <Container fluid className="analytics-page">
        <Alert variant="danger" className="mt-4">
          {error}
          <Button
            variant="outline-danger"
            size="sm"
            className="ms-2"
            onClick={loadAnalyticsData}
          >
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="analytics-page">
      <div className="analytics-header">
        <div>
          <div className="d-flex align-items-center gap-3 mb-3">
            <h2 className="mb-0">Analytics Dashboard</h2>
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip>Refresh data</Tooltip>}
            >
              <Button
                variant="outline-secondary"
                size="lg"
                onClick={handleRefresh}
                disabled={loading || refreshing}
                className="d-flex align-items-center gap-2"
              >
                {refreshing ? (
                  <Spinner size="sm"/>
                ) : (
                  <FaSync className={refreshing ? 'fa-spin' : ''}/>
                )}
              </Button>
            </OverlayTrigger>
          </div>
          <div className="d-flex align-items-center justify-content-center gap-2">
          {lastUpdated && (
            <small className="text-muted">
                <FaClock className="last-updated-icon"/>
                Last updated: {getRelativeTime(lastUpdated)}
            </small>
          )}
            {dataStatus === 'loaded' && (
              <Badge bg="success" className="d-flex align-items-center gap-1">
                <FaCheckCircle size={12}/>
                Data Loaded
              </Badge>
            )}
            {dataStatus === 'error' && (
              <Badge bg="danger" className="d-flex align-items-center gap-1">
                <FaExclamationTriangle size={12}/>
                Error
              </Badge>
            )}
            {dataStatus === 'empty' && (
              <Badge bg="warning" className="d-flex align-items-center gap-1">
                <FaInfoCircle size={12}/>
                No Data
              </Badge>
            )}
          </div>
        </div>
      </div>

      <AnalyticsFilters
        onFiltersChange={handleFiltersChange}
        loading={loading}
      />

      {initialLoading && (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" size="lg">
            <span className="visually-hidden">Loading analytics data...</span>
          </Spinner>
          <p className="mt-3 text-muted">Loading analytics data...</p>
        </div>
      )}

      {dataStatus === 'empty' && !initialLoading && (
        <div className="text-center py-5">
          <div className="mb-4">
            <FaChartLine size={64} className="text-muted"/>
          </div>
          <h4 className="text-muted">No Data Available</h4>
          <p className="text-muted">No analytics data found for the selected filters.</p>
          <Button
            variant="primary"
            onClick={handleRefresh}
            className="mt-3"
          >
            <FaSync className="me-2"/>
            Refresh Data
          </Button>
        </div>
      )}

      {dataStatus === 'loaded' && data.kpis && (
        <div className="analytics-tabs-container">
          <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
            <Row>
              <Col lg={12}>
                <Nav variant="tabs" className="analytics-tabs">
                  <Nav.Item>
                    <Nav.Link eventKey="overview" className="analytics-tab">
                      <FaChartLine className="me-2"/>
                      System Overview
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="bookings" className="analytics-tab">
                      <FaCalendarCheck className="me-2"/>
                      Bookings & Revenue
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="grounds" className="analytics-tab">
                      <FaMapMarkerAlt className="me-2"/>
                      Ground Performance
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="users" className="analytics-tab">
                      <FaUsers className="me-2"/>
                      User Activity
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
            </Col>
          </Row>

            <Tab.Content>
              <Tab.Pane eventKey="overview">
                <div className="d-flex justify-content-end mb-3">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleExport('kpis', 'csv')}
                    disabled={loading || exportLoading}
                  >
                    Export CSV
                  </Button>
                </div>
                <SystemOverviewTab/>
              </Tab.Pane>
              <Tab.Pane eventKey="bookings">
                <div className="d-flex justify-content-end mb-3">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleExport('booking_trends', 'csv')}
                    disabled={loading || exportLoading}
                  >
                    Export CSV
                  </Button>
                </div>
                <BookingsRevenueTab/>
              </Tab.Pane>
              <Tab.Pane eventKey="grounds">
                <div className="d-flex justify-content-end mb-3">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleExport('ground_performance', 'csv')}
                    disabled={loading || exportLoading}
                  >
                    Export CSV
                  </Button>
                </div>
                <GroundPerformanceTab/>
              </Tab.Pane>
              <Tab.Pane eventKey="users">
                <div className="d-flex justify-content-end mb-3">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleExport('user_activity', 'csv')}
                    disabled={loading || exportLoading}
                  >
                    Export CSV
                  </Button>
                </div>
                <UserActivityTab/>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </div>
      )}

      <DrillDownModal
        show={drillDownModal.show}
        onHide={handleCloseDrillDown}
        type={drillDownModal.type}
        title={drillDownModal.title}
        filters={filters}
      />
    </Container>
  );
};

export default Analytics;